import { Controller } from "@hotwired/stimulus"
import { template, debounce } from "lodash"
import { useClickOutside } from "stimulus-use"

export default class extends Controller {
  static targets = ["input", "list", "menu", "searchResultTemplate", "selectedList", "selectedTemplate", "hiddenInputs", "hiddenInputTemplate"]

  static values = {
    departmentId: String,
    invoiceBankAccountVersionIds: Array,
    newRecord: Boolean,
    correctionSourceAccounts: Array,
    correction: Boolean
  }

  availableAccounts = []
  selectedAccounts = []
  invoiceOriginalAccountIds = []
  isInitialLoad = true
  lastSearchResults = []
  accountsLoadFailed = false
  searchResultTemplate = null
  selectedTemplate = null
  eventCreated = false
  loadAbortController = null

  connect() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")

      this.searchResultTemplate = template(this.searchResultTemplateTarget.innerHTML)
      this.selectedTemplate = template(this.selectedTemplateTarget.innerHTML)

      this.triggerSearch = debounce(this.triggerSearch, 300).bind(this)

      useClickOutside(this, {
        element: this.menuTarget
      })
    }

    // Bug D1: filtrujemy puste stringi, bo nowa faktura ma [""] a nie []
    this.invoiceOriginalAccountIds = (this.invoiceBankAccountVersionIdsValue || []).filter(id => id !== '')

    this.loadBankAccounts()

    window.addEventListener("bank-account:create", this.selectCreatedAccount.bind(this))
  }

  departmentIdValueChanged() {
    if (!this.isInitialLoad) {
      this.selectedAccounts = []
    }

    this.loadBankAccounts()
  }

  async loadBankAccounts() {
    this.loadAbortController?.abort()
    this.loadAbortController = new AbortController()
    const { signal } = this.loadAbortController
    this.accountsLoadFailed = false

    const { accounts, error } = this.departmentIdValue
      ? await this.fetchDepartmentAccounts(signal)
      : { accounts: [], error: null }
    if (signal.aborted) return

    if (error) {
      console.error("Error loading bank accounts", error)
      this.accountsLoadFailed = true
    }

    this.availableAccounts = accounts

    if (error || !this.departmentIdValue) {
      // Hidden inputy z rachunkami renderuje wyłącznie JS (server-side jest tu pusty div),
      // więc bez tej ścieżki awaria listy kont działu zapisałaby fakturę bez rachunków.
      if (this.isInitialLoad) {
        await this.loadExistingInvoiceAccounts(signal)
        if (signal.aborted) return
      }
    } else if (this.invoiceOriginalAccountIds.length > 0 && this.isInitialLoad) {
      this.addSelectedAccounts(accounts.filter(account =>
        this.invoiceOriginalAccountIds.includes(account.bank_account_version_id)
      ))

      const foundAccountIds = this.selectedAccounts.map(acc => acc.bank_account_version_id)
      const missingAccountIds = this.invoiceOriginalAccountIds.filter(id =>
        !foundAccountIds.includes(id)
      )

      if (missingAccountIds.length > 0) {
        await this.loadMissingAccounts(missingAccountIds, signal)
        if (signal.aborted) return
      }
    } else if (!this.correctionValue) {
      // Korekta dziedziczy rachunki z faktury korygowanej — gdy nie ma odziedziczonych kont
      // (korygowana ich nie miała), NIE zaciągamy domyślnych kont działu; selectedAccounts
      // zostaje puste. Dla zwykłych faktur nadal bierzemy show_on_invoice.
      this.addSelectedAccounts(accounts.filter(account => account.show_on_invoice))
    }

    this.isInitialLoad = false
    this.updateDisplay()
  }

  // Zwraca konta działu oraz błąd (jeśli wystąpił) — nie rzuca, żeby renderowanie hidden inputów
  // nigdy nie zależało od powodzenia żądania. Odpowiedź niebędąca tablicą jest traktowana jak błąd.
  async fetchDepartmentAccounts(signal) {
    try {
      const headers = this.requestHeaders()

      const response = await fetch(`/departments/${this.departmentIdValue}/bank_accounts`, {
        method: 'GET',
        headers,
        signal
      })

      if (!response.ok) {
        return { accounts: [], error: new Error(`HTTP error! status: ${response.status}`) }
      }

      const data = await response.json()
      if (!Array.isArray(data)) {
        return { accounts: [], error: new Error("Unexpected response body, expected an array") }
      }

      return { accounts: data, error: null }
    } catch (error) {
      return { accounts: [], error: error.name === 'AbortError' ? null : error }
    }
  }

  async triggerSearch(event) {
    const query = event.target.value.toLowerCase().trim()
    this.listTarget.innerHTML = ""

    try {
      const searchResults = await this.fetchBankAccounts(query)
      this.showSearchResults(searchResults)
    } catch (error) {
      console.error("Error searching bank accounts:", error)
    }
  }

  requestHeaders() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return csrfToken
      ? { Accept: 'application/json', 'X-CSRF-Token': csrfToken }
      : { Accept: 'application/json' }
  }

  async fetchBankAccounts(query = '') {
    const headers = this.requestHeaders()

    const alreadyAddedIds = this.selectedAccounts.map(account => account.bank_account_version_id)
    const params = new URLSearchParams({
      rows: '10',
      name: query,
      exclude_ids: alreadyAddedIds.join(',')
    })

    let url
    if (!this.departmentIdValue || this.departmentIdValue === '' || this.departmentIdValue === 'null') {
      url = '/find_bank_accounts'
    } else {
      url = `/departments/${this.departmentIdValue}/bank_accounts`
    }

    const response = await fetch(`${url}?${params}`, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    return Array.isArray(data)
      ? data.map(item => this.normalizeAccount(item))
      : []
  }

  normalizeAccount(item) {
    return {
      id: item.bank_account_version_id || item.id,
      bank_account_version_id: item.bank_account_version_id || item.id,
      bank_account_id: item.bank_account_id,
      name: item.name,
      bank_account_number: item.bank_account_number,
      bank_name: item.bank_name || '',
      bank_swift: item.bank_swift || '',
      bank_currency: item.bank_currency,
      show_on_invoice: item.show_on_invoice || false,
      own_bank_account: item.own_bank_account || false,
      factor_bank_account: item.factor_bank_account || false
    }
  }

  async loadExistingInvoiceAccounts(signal) {
    if (this.invoiceOriginalAccountIds.length === 0) {
      this.selectedAccounts = []
      return
    }

    await this.loadMissingAccounts(this.invoiceOriginalAccountIds, signal)
  }

  async loadMissingAccounts(accountIds, signal) {
    // Seed korekty jest lokalny (dane wyrenderowane server-side) — dokładamy go przed fetchem,
    // żeby ani awaria API (5xx/403), ani przerwanie żądania nie zostawiły korekty bez rachunków.
    this.addSelectedAccounts(this.seedAccountsFor(accountIds))

    const { foundFromApi, error } = await this.fetchAccountsByIds(accountIds, signal)
    if (signal?.aborted) return

    if (error) {
      console.error("Error loading missing accounts:", error)
      this.accountsLoadFailed = true
    }

    this.addSelectedAccounts(foundFromApi)
  }

  // Stimulus parsuje data-atrybut dopiero przy odczycie wartości — uszkodzony JSON rzuciłby
  // wyjątkiem w środku ścieżki ratunkowej, zostawiając formularz bez hidden inputów.
  seedAccountsFor(accountIds) {
    let sourceAccounts = []

    try {
      if (!this.hasCorrectionSourceAccountsValue) return []

      sourceAccounts = this.correctionSourceAccountsValue
    } catch (error) {
      console.error("Error reading correction source accounts", error)
      return []
    }

    return sourceAccounts
      .filter(account => accountIds.includes(account.bank_account_version_id))
      .map(account => this.normalizeAccount(account))
  }

  addSelectedAccounts(accounts) {
    const selectedIds = new Set(this.selectedAccounts.map(a => a.bank_account_version_id))

    accounts.forEach(account => {
      if (selectedIds.has(account.bank_account_version_id)) return

      selectedIds.add(account.bank_account_version_id)
      this.selectedAccounts.push(account)
    })
  }

  // Zwraca konta znalezione przez /find_bank_accounts?include_ids, oraz błąd (jeśli wystąpił).
  // 404 = endpoint nie znalazł żadnego z kont (np. konto odpięte od działu, user bez uprawnień
  // do wszystkich działów) — traktujemy jak pustą listę, nie jak błąd. Pozostałe statusy, odpowiedź
  // niebędąca tablicą oraz błędy sieci wracają w `error` zamiast rzucać.
  async fetchAccountsByIds(accountIds, signal) {
    try {
      const headers = this.requestHeaders()

      const params = new URLSearchParams({
        rows: '20',
        name: '',
        include_ids: accountIds.join(',')
      })

      const response = await fetch(`/find_bank_accounts?${params}`, {
        method: 'GET',
        headers,
        signal
      })

      if (!response.ok && response.status !== 404) {
        return { foundFromApi: [], error: new Error(`HTTP error! status: ${response.status}`) }
      }

      const data = response.ok ? await response.json() : []
      if (!Array.isArray(data)) {
        return { foundFromApi: [], error: new Error("Unexpected response body, expected an array") }
      }

      const foundFromApi = data
        .map(item => this.normalizeAccount(item))
        .filter(account => accountIds.includes(account.bank_account_version_id))

      return { foundFromApi, error: null }
    } catch (error) {
      return { foundFromApi: [], error }
    }
  }

  async onFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length > 0) {
      await this.triggerSearch({ target: this.inputTarget })
    } else {
      await this.showAllAvailableAccounts()
    }
  }

  async showAllAvailableAccounts() {
    try {
      const accounts = await this.fetchBankAccounts("")
      this.showSearchResults(accounts)
    } catch (error) {
      console.error("Error loading all accounts:", error)
      this.hideSearchResults()
    }
  }

  showSearchResults(accounts) {
    this.lastSearchResults = accounts
    this.listTarget.innerHTML = ""

    const fragment = document.createDocumentFragment()
    accounts.forEach((account, index) => {
      const html = this.searchResultTemplate({
        index,
        ...account
      })
      const temp = document.createElement('div')
      temp.innerHTML = html
      fragment.appendChild(temp.firstElementChild)
    })
    this.listTarget.appendChild(fragment)

    this.menuTarget.classList.remove("hidden")
  }

  hideSearchResults() {
    this.menuTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
  }

  // Akcja wisi na trwałym kontenerze listy (`list`), nie na pozycjach: pozycje wstawia
  // showSearchResults z szablonu, a Stimulus wiąże `data-action` nowych węzłów dopiero po
  // przetworzeniu mutacji przez MutationObserver — klik w pozycję trafiony w tym okienku
  // przepada bez śladu. Klikniętą pozycję wyznaczamy przez closest, bo przy delegacji
  // event.params należałoby do węzła z akcją (listy), a nie do pozycji.
  selectAccount(event) {
    const option = event.target.closest("[data-dropdown-invoice-bank-account-version-id-param]")
    if (!option) return // klik w margines listy albo poza pozycją

    event.preventDefault()
    event.stopPropagation()
    const versionId = Number(option.dataset.dropdownInvoiceBankAccountVersionIdParam)
    // availableAccounts zasilają wyłącznie konta działu — faktura bez działu wyszukuje przez
    // /find_bank_accounts, więc klikniętej pozycji trzeba szukać także w wynikach wyszukiwania.
    const selectedAccount = [...this.availableAccounts, ...this.lastSearchResults].find(account =>
      account.bank_account_version_id === versionId
    )

    if (selectedAccount) {
      this.accountsLoadFailed = false
      this.addSelectedAccounts([selectedAccount])
      this.updateDisplay()
    } else {
      console.error("Selected bank account not found in available accounts", versionId)
    }

    this.inputTarget.value = ""

    requestAnimationFrame(() => {
      this.inputTarget.blur()
      this.hideSearchResults()
    })
  }

  // Ta sama delegacja co w selectAccount — przycisk usuwania wstawia updateSelectedList
  // z szablonu, więc akcja wisi na trwałym kontenerze `selectedList`.
  removeAccount(event) {
    const option = event.target.closest("[data-dropdown-invoice-bank-account-version-id-param]")
    if (!option) return // klik w treść wiersza, poza przyciskiem usuwania

    const versionId = Number(option.dataset.dropdownInvoiceBankAccountVersionIdParam)
    this.accountsLoadFailed = false
    this.selectedAccounts = this.selectedAccounts.filter(account =>
      account.bank_account_version_id !== versionId
    )
    this.updateDisplay()
  }

  getAvailableAccounts() {
    const selectedIds = this.selectedAccounts.map(account => account.bank_account_version_id)
    return this.availableAccounts.filter(account => !selectedIds.includes(account.bank_account_version_id))
  }

  updateDisplay() {
    this.updateSelectedList()
    this.updateHiddenInputs()
    this.updateRemoveButtonVisibility()
  }

  updateRemoveButtonVisibility() {
    const isLastAccount = this.selectedAccounts.length <= 1
    const deptHasShowOnInvoice = this.availableAccounts.some(a => a.show_on_invoice)
    const shouldHide = this.newRecordValue && isLastAccount && deptHasShowOnInvoice

    this.selectedListTarget.querySelectorAll("button[data-dropdown-invoice-bank-account-version-id-param]").forEach(btn => {
      btn.classList.toggle("hidden", shouldHide)
    })
  }

  updateSelectedList() {
    this.selectedListTarget.innerHTML = ""

    if (this.selectedAccounts.length === 0) {
      return
    }

    const fragment = document.createDocumentFragment()
    this.selectedAccounts.forEach(account => {
      const html = this.selectedTemplate(account)
      const temp = document.createElement('div')
      temp.innerHTML = html
      fragment.appendChild(temp.firstElementChild)
    })
    this.selectedListTarget.appendChild(fragment)
  }

  // Pusta wartość kasuje rachunki faktury przy zapisie, więc gdy żadnego konta nie udało się
  // wczytać z powodu awarii API, odtwarzamy przypisania z id wyrenderowanych server-side.
  hiddenInputVersionIds() {
    if (this.selectedAccounts.length > 0) {
      return this.selectedAccounts.map(account => account.bank_account_version_id)
    }

    return this.accountsLoadFailed ? this.invoiceOriginalAccountIds : []
  }

  updateHiddenInputs() {
    this.hiddenInputsTarget.innerHTML = ""

    const versionIds = this.hiddenInputVersionIds()
    const fragment = document.createDocumentFragment()

    if (versionIds.length === 0) {
      const input = this.hiddenInputTemplateTarget.content.cloneNode(true).querySelector('input')
      input.value = ""
      fragment.appendChild(input)
    } else {
      versionIds.forEach(versionId => {
        const input = this.hiddenInputTemplateTarget.content.cloneNode(true).querySelector('input')
        input.value = versionId
        fragment.appendChild(input)
      })
    }

    this.hiddenInputsTarget.appendChild(fragment)
  }

  disconnect() {
    this.loadAbortController?.abort()
  }

  clickOutside(event) {
    event.preventDefault()
    if (this.inputTarget.contains(event.target)) return

    if (!this.hasMenuTarget) return

    if (!this.menuTarget.classList.contains("hidden")) {
      this.hideSearchResults()
      this.inputTarget.blur()
    }
  }

  selectCreatedAccount(event) {
    const newAccount = event.detail.bank_account
    if (newAccount) {
      this.selectedAccounts.push(newAccount)
      this.updateDisplay()
      this.inputTarget.value = ""
    } else { console.error("Error added new account") }
  }
}
